import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {GenericModal} from '@techsee/techsee-ui-common/lib/modals/generic';
import Translate from '../../components/translate/Translate';
import {ChangeSettingsModalController} from './ChangeSettingsModal.controller';
import {PasswordErrorType} from './ChangeSettingsModal.constants';
import classNames from 'classnames';
import './ChangeSettingsModal.scss';

interface ChangeSettingsModalProps {
    ctrl: ChangeSettingsModalController;
}

const Header = ({ctrl}: {ctrl: ChangeSettingsModalController}) => {
    return (
        <div className='change-settings-modal-header' dir={ctrl.dir}>
            <h3 className='modal-title'>
                {ctrl.state.passwordOnly ? (
                    <Translate text='CHANGE_PASSWORD.VIEW.CHANGE_PASSWORD' />
                ) : (
                    <Translate text='CHANGE_SETTINGS.VIEW.TITLE' />
                )}
            </h3>
        </div>
    );
};

const Content = observer(
    ({ctrl, formRef}: {ctrl: ChangeSettingsModalController; formRef: React.RefObject<HTMLFormElement>}) => {
        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            ctrl.submit();
        };

        return (
            <form
                ref={formRef}
                id='changeSettingsForm'
                name='changeSettingsForm'
                onSubmit={handleSubmit}
                noValidate
                dir={ctrl.dir}
                className='change-settings-modal-form'>
                {/* Messages */}
                {/* New Password Errors - Show only first error */}
                {ctrl.state.formSubmitted && ctrl.newPasswordErrors.required && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.NEW_PASSWORD_REQUIRED' />
                    </div>
                )}

                {!ctrl.state.formSubmitted && ctrl.firstNewPasswordError === PasswordErrorType.Uppercase && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_REQUIRE_UPPERCASE' />
                    </div>
                )}
                {!ctrl.state.formSubmitted && ctrl.firstNewPasswordError === PasswordErrorType.Lowercase && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_REQUIRE_LOWERCASE' />
                    </div>
                )}
                {!ctrl.state.formSubmitted && ctrl.firstNewPasswordError === PasswordErrorType.Digit && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_REQUIRE_DIGIT' />
                    </div>
                )}
                {!ctrl.state.formSubmitted && ctrl.firstNewPasswordError === PasswordErrorType.Special && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_REQUIRE_SPECIAL' />
                    </div>
                )}
                {!ctrl.state.formSubmitted && ctrl.firstNewPasswordError === PasswordErrorType.Default && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_PATTERN' />
                    </div>
                )}
                {!ctrl.state.formSubmitted && ctrl.firstNewPasswordError === PasswordErrorType.Length && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_MIN_LENGTH' />
                        {ctrl.state.accountData?.settings?.passwordSettings?.minLength}
                    </div>
                )}

                {/* Confirm Password Errors */}
                {ctrl.state.formSubmitted && ctrl.confirmPasswordErrors.required && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.NEW_PASSWORD_REPEAT_REQUIRED' />
                    </div>
                )}
                {ctrl.confirmPasswordErrors.pattern && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.PASSWORD_MISMATCH' />
                    </div>
                )}

                {/* General Errors */}
                {ctrl.state.wrongPassword && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.OLD_PASSWORD_INVALID' />
                    </div>
                )}

                {ctrl.state.usedPassword && (
                    <div className='alert alert-danger' role='alert'>
                        <Translate text='CHANGE_SETTINGS.VIEW.USED_PASSWORD' />
                    </div>
                )}

                {/* First Name */}
                {!ctrl.state.passwordOnly && (
                    <div className='form-group'>
                        <label htmlFor='edit-first-name'>
                            <Translate text='CHANGE_SETTINGS.VIEW.FIRST_NAME' />
                        </label>
                        <input
                            type='text'
                            name='edit-first-name'
                            id='edit-first-name'
                            placeholder='First Name'
                            className='form-control'
                            value={ctrl.data.firstName}
                            onChange={(e) => ctrl.setFirstName(e.target.value)}
                            required
                            autoFocus
                            maxLength={32}
                        />
                    </div>
                )}

                {/* Last Name */}
                {!ctrl.state.passwordOnly && (
                    <div className='form-group'>
                        <label htmlFor='edit-last-name'>
                            <Translate text='CHANGE_SETTINGS.VIEW.LAST_NAME' />
                        </label>
                        <input
                            type='text'
                            name='edit-last-name'
                            id='edit-last-name'
                            placeholder='Last Name'
                            className='form-control'
                            value={ctrl.data.lastName}
                            onChange={(e) => ctrl.setLastName(e.target.value)}
                            required
                            maxLength={32}
                        />
                    </div>
                )}

                {/* Old Password */}
                <div className={classNames('form-group', {'has-success': ctrl.isOldPasswordValid})}>
                    <label htmlFor='edit-old-password'>
                        <Translate text='CHANGE_SETTINGS.VIEW.OLD_PASSWORD' />
                    </label>
                    <div className='password-field-wrapper'>
                        <input
                            type={ctrl.state.showOldPassword ? 'text' : 'password'}
                            name='edit-old-password'
                            id='edit-old-password'
                            className='form-control'
                            title='Please enter your current password'
                            value={ctrl.data.oldPassword || ''}
                            onChange={(e) => ctrl.setOldPassword(e.target.value)}
                        />
                        {ctrl.state.passwordOnly && (
                            <div
                                onClick={() => ctrl.toggleShowOldPassword()}
                                className={classNames('show-password-button', 'glyphicon', {
                                    'glyphicon-eye-open': !ctrl.state.showOldPassword,
                                    'glyphicon-eye-close': ctrl.state.showOldPassword
                                })}
                            />
                        )}
                    </div>
                </div>

                {/* New Password */}
                <div className={classNames('form-group', {'has-error': ctrl.hasNewPasswordError})}>
                    <label htmlFor='edit-new-password'>
                        <Translate text='CHANGE_SETTINGS.VIEW.NEW_PASSWORD' />
                    </label>
                    <div className='password-field-wrapper'>
                        <input
                            type={ctrl.state.showNewPassword ? 'text' : 'password'}
                            name='edit-new-password'
                            id='edit-new-password'
                            className='form-control'
                            value={ctrl.data.newPassword || ''}
                            onChange={(e) => ctrl.setNewPassword(e.target.value)}
                            disabled={!ctrl.data.oldPassword}
                            required={!!ctrl.data.oldPassword}
                        />
                        {ctrl.state.passwordOnly && (
                            <div
                                onClick={() => ctrl.toggleShowNewPassword()}
                                className={classNames('show-password-button', 'glyphicon', {
                                    'glyphicon-eye-open': !ctrl.state.showNewPassword,
                                    'glyphicon-eye-close': ctrl.state.showNewPassword
                                })}
                            />
                        )}
                    </div>
                </div>

                {/* Confirm Password */}
                <div className={classNames('form-group', {'has-error': ctrl.hasConfirmPasswordError})}>
                    <label htmlFor='edit-confirm-password'>
                        <Translate text='CHANGE_SETTINGS.VIEW.CONFIRM_PASSWORD' />
                    </label>
                    <div className='password-field-wrapper'>
                        <input
                            type={ctrl.state.showConfirmPassword ? 'text' : 'password'}
                            name='edit-confirm-password'
                            id='edit-confirm-password'
                            className='form-control'
                            title='Please re-type new password'
                            value={ctrl.data.confirmPassword || ''}
                            onChange={(e) => ctrl.setConfirmPassword(e.target.value)}
                            disabled={!ctrl.data.oldPassword || !ctrl.data.newPassword}
                            required={!!ctrl.data.oldPassword && !!ctrl.data.newPassword}
                        />
                        {ctrl.state.passwordOnly && (
                            <div
                                onClick={() => ctrl.toggleShowConfirmPassword()}
                                className={classNames('show-password-button', 'glyphicon', {
                                    'glyphicon-eye-open': !ctrl.state.showConfirmPassword,
                                    'glyphicon-eye-close': ctrl.state.showConfirmPassword
                                })}
                            />
                        )}
                    </div>
                </div>
            </form>
        );
    }
);

const Footer = observer(
    ({ctrl, formRef}: {ctrl: ChangeSettingsModalController; formRef: React.RefObject<HTMLFormElement>}) => {
        const handleSubmit = () => {
            if (formRef.current) {
                formRef.current.requestSubmit();
            } else {
                ctrl.submit();
            }
        };

        return (
            <div className='change-settings-modal-footer' dir={ctrl.dir}>
                <button type='button' className='btn btn-default margin-right-10' onClick={() => ctrl.dismiss()}>
                    <Translate text='CHANGE_SETTINGS.VIEW.CANCEL' />
                </button>
                <button
                    type='button'
                    className='btn btn-primary'
                    onClick={handleSubmit}
                    disabled={ctrl.state.saving || !ctrl.hasChanges}>
                    <Translate text='CHANGE_SETTINGS.VIEW.SAVE' />
                </button>
            </div>
        );
    }
);

@observer
export class ChangeSettingsModal extends TechseeBaseComponent<ChangeSettingsModalProps> {
    private formRef = React.createRef<HTMLFormElement>();

    renderInternal(): JSX.Element {
        const {ctrl} = this.props;

        return (
            <GenericModal
                className='modal-dialog change-settings-modal'
                header={() => <Header ctrl={ctrl} />}
                content={() => <Content ctrl={ctrl} formRef={this.formRef} />}
                toolbar={() => <Footer ctrl={ctrl} formRef={this.formRef} />}
                show={ctrl.state.isVisible}
                onHideHandler={() => ctrl.dismiss()}
                closeOnEscape={!ctrl.state.passwordOnly}
                isCloseVisible={false}
            />
        );
    }
}
